// Pure, framework-free voice-quota math. The server (cloudflare-worker
// `voice-quota.ts` + the `/voice/record-usage` endpoint) is the source of truth
// for `seconds_used_this_week` + the lockout anchor; this module only DERIVES
// display/timer values from the user doc. No Firestore, no React.
//
// Reset model (exhaustion-anchored): the weekly allotment does NOT reset on a
// calendar boundary. The clock starts only when the user RUNS OUT — at that
// moment the server stamps the lockout anchor and the quota refills
// SECONDS_PER_WEEK later. A user who never hits the cap keeps their partial usage
// (no countdown running) until they do run out.

/** Default weekly free voice cap (seconds) when the user doc omits it. Mirror of
 *  the server-driven `free_seconds_per_week`; kept as a fallback for the rollout
 *  overlap window (spec §3.1). */
export const FREE_WEEKLY_VOICE_SECONDS_FALLBACK = 300;

/** Lockout length — the quota refills this long after the moment of exhaustion. */
const SECONDS_PER_WEEK = 7 * 24 * 60 * 60;

export interface VoiceQuotaInformation {
  seconds_used_this_week: number;
  /**
   * Epoch SECONDS of the moment the user most recently EXHAUSTED their free
   * voice (the lockout anchor), or 0 when they have NOT exhausted the current
   * allotment. The quota refills SECONDS_PER_WEEK after this moment.
   *
   * NOTE: still named `week_start_at` for wire-compat with the Firestore doc +
   * the worker; its meaning is "lockout anchor", NOT a calendar-week Monday.
   */
  week_start_at: number;
  free_seconds_per_week: number;
  lifetime_depleted_count: number;
}

function capSeconds(info: VoiceQuotaInformation | null | undefined): number {
  const cap = info?.free_seconds_per_week;
  return typeof cap === "number" && cap > 0 ? cap : FREE_WEEKLY_VOICE_SECONDS_FALLBACK;
}

/** The lockout anchor (epoch s) — the moment the allotment was exhausted — or 0
 *  when the user still has free voice left (no countdown running). */
function lockoutAnchorSeconds(info: VoiceQuotaInformation | null | undefined): number {
  return info && typeof info.week_start_at === "number" && info.week_start_at > 0
    ? info.week_start_at
    : 0;
}

/** True when a lockout is active AND its window has fully elapsed — i.e. the
 *  allotment has refilled but the server hasn't lazily written the reset yet. */
function isLockoutExpired(info: VoiceQuotaInformation | null | undefined, nowMs: number): boolean {
  const anchor = lockoutAnchorSeconds(info);
  if (anchor <= 0) return false; // no active lockout → nothing to expire
  return Math.floor(nowMs / 1000) >= anchor + SECONDS_PER_WEEK;
}

/** Seconds of free voice the user has left. The clock only runs once they hit 0:
 *   - not exhausted (anchor 0)        → cap − used
 *   - exhausted, lockout still active → 0 (used is clamped to cap server-side)
 *   - exhausted, lockout elapsed      → full cap (refilled; server reset pending) */
export function computeVoiceSecondsRemaining(info: VoiceQuotaInformation | null | undefined, nowMs: number): number {
  const cap = capSeconds(info);
  if (!info) return cap;
  if (isLockoutExpired(info, nowMs)) return cap;
  const used = typeof info.seconds_used_this_week === "number" ? info.seconds_used_this_week : 0;
  return Math.max(0, cap - used);
}

/** Wall-clock (ms) when the quota next resets, or 0 when no reset is scheduled
 *  (the user still has free voice and hasn't started a lockout).
 *
 *  When a lockout is active the reset is exactly SECONDS_PER_WEEK after the
 *  exhaustion anchor. Fallback: if the doc shows the user at/over the cap but the
 *  server hasn't stamped the anchor yet (the call-end deduction is still in flight
 *  right after a mid-call cap-out), treat the lockout as starting now so the
 *  "resets {day}" copy is correct immediately. */
export function computeVoiceQuotaResetsAtMs(info: VoiceQuotaInformation | null | undefined, nowMs: number): number {
  const anchor = lockoutAnchorSeconds(info);
  if (anchor > 0) return (anchor + SECONDS_PER_WEEK) * 1000;
  const cap = capSeconds(info);
  const used = info && typeof info.seconds_used_this_week === "number" ? info.seconds_used_this_week : 0;
  if (used >= cap) return (Math.floor(nowMs / 1000) + SECONDS_PER_WEEK) * 1000;
  return 0;
}
